import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { AiOutlinePlus } from "react-icons/ai";
import animatedScroll from "animated-scroll-to";
import {
  Card,
  SectionContent,
  Text,
  SearchControl,
  Loading,
} from "../../../../components";
import { useParceirosCredenciados } from "../../../../hooks";
import { applyKey, formatCnpj, parseISO } from "../../../../utils";
import * as S from "./EcontrarParceiro.Style";

const alfabeto = "0"
  .repeat(26)
  .split("")
  .map((...[, n]) => String.fromCharCode(65 + n))
  .map((letter) => ({ letter, label: letter }))
  .concat({ letter: "([^a-zA-Z])", label: "#" });

const ParceiroDetails = ({ details, active, onToggleItem }) => {
  const { nomeFantasia, razaoSocial, cnpj, matriz, registroUnicad } = details;

  const ref = useRef(null);

  const ontoggleHandle = useCallback(
    (evt) => {
      evt.preventDefault();
      animatedScroll(ref.current, {
        verticalOffset: -75,
        easing: (t) => t,
      });

      if (onToggleItem) {
        onToggleItem();
      }
    },
    [onToggleItem]
  );

  return (
    <S.Details ref={ref}>
      <S.DetailsCard
        as={Card}
        header={
          <S.DetailsCardHeader
            as={Text}
            elementType="a"
            onClick={ontoggleHandle}
          >
            {nomeFantasia}
            <S.DetailsCardHeaderIcon as={AiOutlinePlus} />
          </S.DetailsCardHeader>
        }
        className={active ? "active" : null}
      >
        <S.DetailsCardData>
          <S.CaptionText textType="caption">Razão Social</S.CaptionText>
          <Text>{razaoSocial}</Text>
          <S.CaptionText textType="caption">CNPJ</S.CaptionText>
          <Text>{formatCnpj(cnpj)}</Text>
          <S.CaptionText textType="caption">Matriz</S.CaptionText>
          <Text>{matriz}</Text>
          <S.CaptionText textType="caption">
            Data de Registro UNICAD
          </S.CaptionText>
          <Text>{parseISO(registroUnicad).toLocaleDateString()}</Text>
        </S.DetailsCardData>
      </S.DetailsCard>
    </S.Details>
  );
};

ParceiroDetails.defaultProps = {
  active: false,
  onToggleItem: PropTypes.func,
};

ParceiroDetails.propTypes = {
  details: PropTypes.shape({
    nomeFantasia: PropTypes.string.isRequired,
    razaoSocial: PropTypes.string.isRequired,
    cnpj: PropTypes.string.isRequired,
    matriz: PropTypes.string.isRequired,
    registroUnicad: PropTypes.string.isRequired,
  }).isRequired,
  active: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  onToggleItem: PropTypes.func,
};

const ListaParceiros = ({ parceiros }) => {
  const [parceiroVisivel, setParceiroVisivel] = useState(-1);

  useEffect(() => {
    return () => {
      setParceiroVisivel(-1);
    };
  }, [parceiros]);

  return parceiros.length ? (
    <S.Accordion>
      {applyKey(parceiros, (parceiro, idx) => {
        return (
          <ParceiroDetails
            details={parceiro}
            active={idx === parceiroVisivel}
            onToggleItem={() => setParceiroVisivel(idx)}
          />
        );
      })}
    </S.Accordion>
  ) : (
    <>Nenhum parceiro foi encontrado</>
  );
};

ListaParceiros.defaultProps = {
  parceiros: [],
};

ListaParceiros.propTypes = {
  parceiros: PropTypes.arrayOf(ParceiroDetails.propTypes.details),
};

const EncontrarParceiro = ({ setDataAtualizacao }) => {
  const {
    parceiros,
    dataAtualizacao,
    loading,
    listarParceiros,
  } = useParceirosCredenciados();

  const [search, setSearch] = useState("");
  const [letterSelected, setLetterSelected] = useState("A");
  const [parceirosVisiveis, setParceirosVisiveis] = useState([]);

  const filtrarParceirosVisiveis = useCallback(
    async (listaParceiros, term) =>
      setParceirosVisiveis(
        listaParceiros.filter(({ nomeFantasia }) =>
          new RegExp(term, "ig").test(nomeFantasia)
        )
      ),
    []
  );

  const filterByLetter = useCallback(
    async (listaParceiros) =>
      filtrarParceirosVisiveis(listaParceiros, `^${letterSelected}`),
    [filtrarParceirosVisiveis, letterSelected]
  );

  const changeLetterHandle = useCallback(
    (letter) => {
      setLetterSelected(letter);
      setSearch("");
      filterByLetter(parceiros);
    },
    [filterByLetter, parceiros]
  );

  const onSearchHandle = useCallback(
    async (termo) => {
      if (search && !termo) {
        filterByLetter(parceiros);
      }

      setSearch(termo);
    },
    [filterByLetter, parceiros, search]
  );

  useEffect(() => {
    filterByLetter(parceiros);
  }, [filterByLetter, parceiros]);

  useEffect(() => {
    if (search) {
      filtrarParceirosVisiveis(parceiros, search);
    }
  }, [filterByLetter, filtrarParceirosVisiveis, parceiros, search]);

  useEffect(() => {
    setDataAtualizacao(dataAtualizacao);
  }, [dataAtualizacao, setDataAtualizacao]);

  useEffect(() => {
    listarParceiros();
  }, [listarParceiros]);

  return (
    <Loading loading={loading}>
      <SectionContent>
        <Text>
          Para encontrar um parceiro, digite o nome da empresa ou selecione a
          letra inicial.
        </Text>
        <S.InputSearch
          as={SearchControl}
          name="Buscar Parceiros"
          placeholder="Qual parceiro você procura?"
          onSearchButtonClick={onSearchHandle}
        />
        <S.AlphabetContent>
          {applyKey(alfabeto, ({ label, letter }) => (
            <S.AlphabetLetter
              selected={letterSelected === letter && !search}
              onClick={() => changeLetterHandle(letter)}
            >
              {label}
            </S.AlphabetLetter>
          ))}
        </S.AlphabetContent>
        <ListaParceiros parceiros={parceirosVisiveis} />
      </SectionContent>
    </Loading>
  );
};

EncontrarParceiro.propTypes = {
  setDataAtualizacao: PropTypes.func.isRequired,
};

export default EncontrarParceiro;
